<template>
  <vue-final-modal
    v-model="isOpen"
    classes="modal-overlay-container"
    content-class="modern-modal-content"
    @before-close="closeModal"
  >
    <div class="modal-header">
      <div class="header-text-container">
        <div class="title-wrapper">
          <div class="title-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
          </div>
          <h5 class="modal-title">Assign Trainings</h5>
        </div>
      </div>
      <button type="button" class="close-button" @click="closeModal" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div class="modal-body p-0">

      <div class="search-area p-3 border-bottom">
        <div class="input-with-icon">
          <div class="input-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="Search library trainings..."
            autofocus
          />
        </div>
      </div>

      <div class="module-list-container">
        <div v-if="filteredModules.length === 0" class="text-center text-muted p-4">
          No available trainings found.
        </div>

        <div
          v-for="mod in filteredModules"
          :key="mod.id"
          class="module-selection-item"
          @click="toggleSelection(mod.id)"
        >
          <div class="form-check custom-checkbox m-0 p-0 d-flex align-items-center gap-3 pointer">
            <input
              class="form-check-input m-0 flex-shrink-0"
              type="checkbox"
              :value="mod.id"
              v-model="selectedModuleIds"
              @click.stop
            >
            <div class="d-flex flex-column" style="overflow: hidden;">
              <span class="fw-bold text-dark text-truncate" style="font-size: 0.95rem;">{{ mod.name }}</span>
              <span class="text-muted small text-truncate">
                <i :class="mod.type === 'video' ? 'fas fa-play-circle' : 'fas fa-laptop-code'" class="me-1"></i>
                {{ mod.type === 'scorm' ? 'SCORM ' + (mod.scorm_version || '') : 'Video File' }}
                • {{ mod.display_path || mod.launch_path }}
              </span>
            </div>
          </div>
        </div>
      </div>

    </div>

    <div class="modal-footer" :class="{'bg-warning-soft': confirmState}">

      <template v-if="!confirmState">
        <div class="me-auto text-muted small fw-bold">
          {{ selectedModuleIds.length }} selected
        </div>
        <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="selectedModuleIds.length === 0"
          @click="handleSaveClick"
        >
          Add Trainings
        </button>
      </template>

      <template v-else>
        <div class="me-auto d-flex align-items-center text-dark small fw-bold">
          <i class="fas fa-exclamation-circle text-warning me-2 fs-5"></i>
          Assign new training to enrolled users?
        </div>
        <button type="button" class="btn btn-secondary bg-white" @click="executeSave(false)">No, Just Add</button>
        <button
          type="button"
          class="btn btn-warning fw-bold text-dark border-0 shadow-sm"
          @click="executeSave(true)"
        >
          Yes, Assign
        </button>
      </template>

    </div>
  </vue-final-modal>
</template>

<script setup>
import { ref, computed, watch, defineEmits, defineProps} from 'vue';

const props = defineProps(['modelValue', 'libraryModules', 'alreadyAssignedIds', 'isOnDemand', 'programStatus']);
const emit = defineEmits(['update:modelValue', 'save']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const searchQuery = ref('');
const selectedModuleIds = ref([]);
const confirmState = ref(false);

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    searchQuery.value = '';
    selectedModuleIds.value = [];
    confirmState.value = false;
  }
});

const filteredModules = computed(() => {
  return props.libraryModules.filter(mod => {
    const notAssignedYet = !props.alreadyAssignedIds.includes(mod.id);
    const matchesSearch = mod.name.toLowerCase().includes(searchQuery.value.toLowerCase());
    const isActive = mod.is_active;

    return notAssignedYet && matchesSearch && isActive;
  });
});

const toggleSelection = (id) => {
  const index = selectedModuleIds.value.indexOf(id);
  if (index === -1) {
    selectedModuleIds.value.push(id);
  } else {
    selectedModuleIds.value.splice(index, 1);
  }
};

const closeModal = () => {
  isOpen.value = false;
  confirmState.value = false;
};

const handleSaveClick = () => {
  if (props.programStatus === 'published' && props.isOnDemand) {
    confirmState.value = true;
  } else {
    executeSave(false);
  }
};

const executeSave = (autoDeploy) => {
  const selectedModules = props.libraryModules.filter(m => selectedModuleIds.value.includes(m.id));
  emit('save', selectedModules, autoDeploy);
  confirmState.value = false;
};
</script>

<style scoped>
:deep(.modal-overlay-container) {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
}

:deep(.modern-modal-content) {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 550px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  border: none !important;
  padding: 0;
  margin: 0 1rem;
  overflow: hidden;
  max-height: 85vh;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
}

.title-wrapper { display: flex; align-items: center; gap: 0.75rem; }
.title-icon { color: #00a0c1; display: flex; align-items: center; justify-content: center; background: rgba(0, 160, 193, 0.1); padding: 8px; border-radius: 8px; }
.modal-title { color: #1e293b; margin: 0; font-size: 1.15rem; font-weight: 700; letter-spacing: -0.01em; }

.close-button {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.2s;
}

.close-button:hover { background-color: #fee2e2; color: #dc3545; transform: rotate(90deg); }

.search-area { background-color: #f8fafc; }
.input-with-icon { position: relative; display: flex; align-items: center; }
.input-icon { position: absolute; left: 12px; color: #94a3b8; z-index: 1; }

.form-control {
  width: 100%;
  padding: 0.6rem 0.6rem 0.6rem 2.2rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s;
}
.form-control:focus { border-color: #00a0c1; box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15); }

.module-list-container { overflow-y: auto; max-height: 350px; }

.module-selection-item {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background-color 0.15s;
}

.module-selection-item:hover { background-color: #f8fafc; }
.module-selection-item:last-child { border-bottom: none; }
.pointer { cursor: pointer; }

.custom-checkbox .form-check-input {
  width: 1.25rem;
  height: 1.25rem;
  border: 1.5px solid #cbd5e1;
  cursor: pointer;
}

.custom-checkbox .form-check-input:checked {
  background-color: #00a0c1;
  border-color: #00a0c1;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.5rem;
  gap: 0.75rem;
  background-color: #ffffff;
  transition: background-color 0.3s ease;
}

.bg-warning-soft {
  background-color: #fffbeb !important;
  border-top-color: #fde68a !important;
}

.btn {
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-primary { background-color: #00a0c1; color: white; border: none; }
.btn-primary:hover:not(:disabled) { background-color: #008da8; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-secondary { background-color: white; color: #64748b; border: 1px solid #e2e8f0; }
.btn-secondary:hover { background-color: #f8fafc; color: #1e293b; }
</style>